import { MoreHorizontal, Eye, Copy, Trash2, Pencil } from "lucide-react";
import { useState } from "react";
import Badge from "../../../components/ui/Badge";
import Sparkline from "../../../components/ui/Sparkline";
import NoDataState from "../../../components/NoDataState";
import { CAMPAIGN_STATUS } from "./campaign.data";

function RowMenu({ onView, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block text-left">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="w-8 h-8 rounded-lg flex items-center justify-center text-text_muted hover:bg-surface_alt hover:text-text_primary transition-colors"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div className="absolute right-0 mt-1 w-40 z-20 rounded-xl border border-border bg-surface py-1 shadow-xl">
            {[
              { label: "View report", icon: Eye, action: onView },
              { label: "Edit", icon: Pencil, action: onView },
              { label: "Duplicate", icon: Copy, action: () => {} },
            ].map((it) => (
              <button
                key={it.label}
                onClick={(e) => { e.stopPropagation(); setOpen(false); it.action?.(); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-text_secondary hover:bg-surface_alt transition-colors"
              >
                <it.icon className="w-3.5 h-3.5" />
                {it.label}
              </button>
            ))}
            <div className="my-1 border-t border-border" />
            <button
              onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete?.(); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium transition-colors hover:bg-danger_bg"
              style={{ color: "var(--color-danger)" }}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ value, suffix, color, muted }) {
  if (muted) return <span className="text-sm text-text_muted">—</span>;
  return (
    <span className="text-sm font-semibold" style={{ color }}>
      {value}{suffix}
    </span>
  );
}

export default function CampaignTable({ rows, onView, onDelete }) {
  if (!rows.length) {
    return (
      <div className="rounded-xl border border-border bg-surface">
        <NoDataState title="No campaigns match your filters" subtitle="Try a different status tab or search term." />
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto rounded-xl border border-border bg-surface"
         style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>
      <table className="w-full">
        <thead>
          <tr className="bg-surface_alt border-b border-border">
            <th className="text-left text-xs font-semibold text-text_muted uppercase tracking-wide px-5 py-3">Campaign</th>
            <th className="text-left text-xs font-semibold text-text_muted uppercase tracking-wide px-4 py-3">Status</th>
            <th className="text-right text-xs font-semibold text-text_muted uppercase tracking-wide px-4 py-3">Recipients</th>
            <th className="text-right text-xs font-semibold text-text_muted uppercase tracking-wide px-4 py-3">Open</th>
            <th className="text-right text-xs font-semibold text-text_muted uppercase tracking-wide px-4 py-3">Click</th>
            <th className="text-center text-xs font-semibold text-text_muted uppercase tracking-wide px-4 py-3">Trend</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((c) => {
            const st = CAMPAIGN_STATUS[c.status] ?? CAMPAIGN_STATUS.draft;
            const idle = c.sent === 0;
            return (
              <tr
                key={c.id}
                onClick={() => onView(c)}
                className="hover:bg-surface_alt transition-colors cursor-pointer"
              >
                <td className="px-5 py-3.5 max-w-xs">
                  <div className="text-sm font-semibold text-text_primary leading-tight truncate">{c.name}</div>
                  <div className="text-xs text-text_muted mt-0.5 truncate">{c.type} · {c.audience}</div>
                </td>
                <td className="px-4 py-3.5">
                  <Badge tone={st.tone} dot>{st.label}</Badge>
                </td>
                <td className="px-4 py-3.5 text-right">
                  <span className="text-sm text-text_secondary mono">
                    {c.recipients > 0 ? c.recipients.toLocaleString() : "—"}
                  </span>
                </td>
                <td className="px-4 py-3.5 text-right">
                  <Metric value={c.openRate} suffix="%" color="var(--color-success)" muted={idle} />
                </td>
                <td className="px-4 py-3.5 text-right">
                  <Metric value={c.clickRate} suffix="%" color="var(--color-info)" muted={idle} />
                </td>
                <td className="px-4 py-3.5">
                  <div className="flex justify-center">
                    {idle
                      ? <span className="text-xs text-text_muted">—</span>
                      : <Sparkline data={c.trend} width={90} height={30} color="var(--color-primary)" />}
                  </div>
                </td>
                <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                  <RowMenu onView={() => onView(c)} onDelete={() => onDelete(c)} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
